import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Plus } from "lucide-react";
import { MDBTooltip } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";

const MENU_MAX_HEIGHT = 240;
const MENU_MIN_WIDTH = 120;
const ESTIMATED_OPTION_HEIGHT = 40;

const CustomDropdown = ({
  options,
  handleSelect,
  selectedOption = null,
  placeholder = "Select or search",
  className = "",
  showLable = true,
  label = "branches",
  labelIcon = null,
  disabled = false,
  showClear = true,
  emptyMessage,
  onSearchChange,
  showIcon = false,
  showCreateAction = false,
  invalid = false,
  menuPortal = false
}) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar" || i18n.dir() === "rtl";

  const getOptionName = (option) => {
    if (!option) return "";
    if (option.displayName) return option.displayName;
    if (isArabic) {
      return (
        option.name?.ar ||
        option.name_ar ||
        option.name?.en ||
        option.name ||
        "Unnamed"
      );
    }
    return option.name?.en || option.name || "Unnamed";
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [temporarilyRemovedOption, setTemporarilyRemovedOption] =
    useState(null);
  const [isFocused, setIsFocused] = useState(false);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  const [isTruncated, setIsTruncated] = useState(false);
  const [menuPos, setMenuPos] = useState({
    top: 0,
    left: 0,
    width: MENU_MIN_WIDTH,
    openUp: false,
  });

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setIsFocused(false);
    setHighlightedIndex(-1);
    setSearchTerm("");
  }, []);

  const updateMenuPosition = useCallback(() => {
    const anchor = inputRef.current;
    if (!anchor?.getBoundingClientRect) return;

    const rect = anchor.getBoundingClientRect();
    const menuWidth = Math.max(MENU_MIN_WIDTH, rect.width);
    let left = rect.left;
    if (left + menuWidth > window.innerWidth) {
      left = Math.max(8, window.innerWidth - menuWidth - 8);
    }

    const optionCount = Math.max(
      1,
      (showCreateAction ? 1 : 0) +
        (searchTerm
          ? options.filter((o) =>
              (o.name?.en || o.name || "")
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            ).length
          : options.length)
    );
    const estimatedHeight = Math.min(
      MENU_MAX_HEIGHT,
      optionCount * ESTIMATED_OPTION_HEIGHT + 8
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedHeight + 12;
    const menuEl = menuRef.current;
    const measuredHeight = menuEl
      ? menuEl.offsetHeight || menuEl.getBoundingClientRect().height
      : estimatedHeight;
    const top = openUp
      ? Math.max(8, rect.top - measuredHeight - 4)
      : rect.bottom + 4;

    setMenuPos({ top, left, width: menuWidth, openUp });
  }, [options, searchTerm, showCreateAction]);

  useEffect(() => {
    if (typeof selectedOption === "string") {
      setSelectedOptionId(selectedOption || null);
    } else if (selectedOption?._id) {
      setSelectedOptionId(selectedOption._id);
    } else {
      setSelectedOptionId(null);
    }
  }, [selectedOption]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current?.contains(event.target) ||
        event.target.closest("[data-custom-dropdown-menu]")
      ) {
        return;
      }

      closeDropdown();
      if (temporarilyRemovedOption) {
        setTemporarilyRemovedOption(null);
        setSelectedOptionId(temporarilyRemovedOption._id);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [temporarilyRemovedOption, closeDropdown]);

  useEffect(() => {
    if (!showDropdown || !menuPortal) return undefined;

    let rafId = null;
    const onScrollOrResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateMenuPosition);
    };

    updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("touchmove", onScrollOrResize, { passive: true });

    const scrollableListeners = [];
    const anchor = inputRef.current;
    if (anchor) {
      let el = anchor.parentElement;
      while (el && el !== document.body) {
        try {
          const cs = getComputedStyle(el);
          if (cs.overflowY !== "visible" && el.scrollHeight > el.clientHeight) {
            el.addEventListener("scroll", onScrollOrResize, { passive: true });
            scrollableListeners.push(el);
          }
        } catch {
          /* ignore */
        }
        el = el.parentElement;
      }
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("touchmove", onScrollOrResize);
      scrollableListeners.forEach((el) =>
        el.removeEventListener("scroll", onScrollOrResize)
      );
    };
  }, [showDropdown, menuPortal, updateMenuPosition]);

  // Truncation tip for long selected labels (Brand/Branch/etc.) — products-style MDBTooltip
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el || isFocused || !selectedOptionId) {
      setIsTruncated(false);
      return undefined;
    }
    const check = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    };
    check();
    const raf = requestAnimationFrame(check);
    window.addEventListener("resize", check);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", check);
    };
  }, [selectedOptionId, selectedOption, options, isFocused, searchTerm, isArabic]);

  const optionMatches = (opt, term) => {
    const termLower = (term || "").toLowerCase();
    const fields = [
      getOptionText(opt),
      opt?.displayName,
      opt?.displayNameAr,
      opt?.name?.en,
      opt?.name?.ar,
      opt?.name_en,
      opt?.name_ar,
      typeof opt?.name === "string" ? opt?.name : undefined,
    ];
    return fields.some((f) => {
      const s = typeof f === "string" ? f : (f ?? "").toString();
      return s.toLowerCase().includes(termLower);
    });
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (typeof onSearchChange === "function") {
      onSearchChange(value);
    }
    if (selectedOptionId) {
      setTemporarilyRemovedOption({
        _id: selectedOptionId,
        name: value,
      });
      setSelectedOptionId(null);
    }
    setSearchTerm(value);
    setShowDropdown(true);
    if (menuPortal) {
      requestAnimationFrame(updateMenuPosition);
    }

    const matchIndex = getFilteredOptions(value).findIndex((o) => optionMatches(o, value));
    setHighlightedIndex(matchIndex >= 0 ? matchIndex : -1);
    if (matchIndex >= 0) scrollToItem(matchIndex);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setSearchTerm("");
    setShowDropdown(true);
    if (typeof onSearchChange === "function") {
      onSearchChange("");
    }
    if (menuPortal) {
      requestAnimationFrame(updateMenuPosition);
    }

    const selectedIndex = getFilteredOptions("").findIndex(
      (o) => o._id === selectedOptionId
    );
    if (selectedIndex >= 0) {
      setHighlightedIndex(selectedIndex);
      setTimeout(() => scrollToItem(selectedIndex), 0);
    } else {
      setHighlightedIndex(0);
    }
  };

  const scrollToItem = (index) => {
    const el = itemRefs.current[index];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  };

  const handleClearClick = () => {
    setSelectedOptionId(null);
    setSearchTerm("");
    setTemporarilyRemovedOption(null);
    closeDropdown();
    handleSelect({ _id: "", name: "" });
  };

  const getFilteredOptions = (term) => {
    const baseOptions = showCreateAction
      ? options.filter((o) => o._id !== "__CREATE_STORE__")
      : options;
    if (!term) return baseOptions;
    return baseOptions.filter((o) =>
      (o.name?.en || o.name || "").toLowerCase().includes(term.toLowerCase())
    );
  };

  const getOptionText = (opt) => {
    if (!opt) return "";
    if (isArabic) {
      return opt.displayNameAr || opt.displayName || opt.name?.ar || opt.name_ar || opt.displayName || opt.name?.en || opt.name || "";
    }
    return opt.displayName || opt.name?.en || opt.name || opt.name_ar || opt.displayNameAr || "";

  };
  const getSelectedName = () => {
    const match = options.find((opt) => opt._id === selectedOptionId);
    return getOptionName(match);
  };

  const filteredOptions = getFilteredOptions(searchTerm);
  const resolvedEmptyMessage = emptyMessage ?? t("No options found");
  const selectedName = getSelectedName();
  const displayValue = isFocused ? searchTerm : selectedName;
  const showTruncationTip =
    !isFocused && !!selectedName && isTruncated;

  const selectOption = (option) => {
    if (!option || selectedOptionId === option._id) return;
    handleSelect(option);
    setSelectedOptionId(option._id === "__CREATE_STORE__" ? null : option._id);
    setSearchTerm("");
    setTemporarilyRemovedOption(null);
    closeDropdown();
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) return;
    const maxIndex = filteredOptions.length - 1;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev < maxIndex ? prev + 1 : 0;
        scrollToItem(next);
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev > 0 ? prev - 1 : maxIndex;
        scrollToItem(next);
        return next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        selectOption(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      closeDropdown();
    }
  };

  const renderMenu = () => (
    <div
      ref={menuRef}
      data-custom-dropdown-menu
      className={[
        "oh-select__menu",
        "custom-dropdown-menu",
        "show",
        "responsive-dropdown-menu",
        menuPortal ? "custom-dropdown-menu--portal" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        menuPortal
          ? {
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
              width: `${menuPos.width}px`,
            }
          : undefined
      }
    >
      {showCreateAction && (
        <>
          <div
            className="oh-select__create custom-dropdown-create-item"
            onClick={() => {
              handleSelect({ _id: "__CREATE_STORE__", name: t("Create Store") });
              setSelectedOptionId(null);
              setSearchTerm("");
              setTemporarilyRemovedOption(null);
              closeDropdown();
            }}
          >
            <Plus size={14} />
            <span>{t("Create Store")}</span>
          </div>
          <div style={{ borderTop: "1px solid #e9ecef", margin: "0 4px 8px" }} />
        </>
      )}
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option, idx) => {
          const isSelected = selectedOptionId === option._id;
          const isHighlighted = idx === highlightedIndex;

          return (
            <div
              key={option._id}
              ref={(el) => {
                itemRefs.current[idx] = el;
              }}
              className={`oh-select__option custom-dropdown-item${
                isSelected ? " active" : ""
              }${isHighlighted ? " highlighted" : ""}`}
              onMouseEnter={() => !isSelected && setHighlightedIndex(idx)}
              onMouseLeave={() => !isSelected && setHighlightedIndex(-1)}
              onClick={() => selectOption(option)}
            >
              {getOptionName(option)}
            </div>
          );
        })
      ) : (
        <div className="oh-select__option custom-dropdown-item disabled">
          {resolvedEmptyMessage}
        </div>
      )}
    </div>
  );

  const menuNode =
    showDropdown &&
    (menuPortal && typeof document !== "undefined"
      ? createPortal(renderMenu(), document.body)
      : renderMenu());

  useEffect(() => {
    if (!showDropdown || !menuPortal) return;
    requestAnimationFrame(updateMenuPosition);
  }, [searchTerm, options.length, showDropdown, menuPortal, updateMenuPosition]);

  const renderLabelIcon = () => {
    if (!showIcon) return null;
    const key = String(labelIcon || label || "").toLowerCase();
    if (key.includes("branch")) {
      return <i className="bi bi-buildings oh-filter-bar__icon" aria-hidden="true" />;
    }
    if (key.includes("store") || key.includes("brand")) {
      return <i className="bi bi-shop oh-filter-bar__icon" aria-hidden="true" />;
    }
    if (key.includes("search")) {
      return <i className="bi bi-search oh-filter-bar__icon" aria-hidden="true" />;
    }
    if (key.includes("calendar") || key.includes("date")) {
      return <i className="bi bi-calendar-event oh-filter-bar__icon" aria-hidden="true" />;
    }
    if (key.includes("category") || key.includes("folder")) {
      return <i className="bi bi-folder oh-filter-bar__icon" aria-hidden="true" />;
    }
    if (key.includes("platform") || key.includes("grid")) {
      return <i className="bi bi-grid oh-filter-bar__icon" aria-hidden="true" />;
    }
    return null;
  };

  return (
    <div
      className={`oh-select custom-dropdown-container d-flex flex-column position-relative ${className}${
        invalid ? " oh-field--invalid" : ""
      }`}
      ref={dropdownRef}
    >
      <div className="custom-dropdown-input-wrapper">
        {showLable && (
          <label htmlFor="filter-status" className="oh-field__label custom-input-label">
            {renderLabelIcon()}
            {t(label)}
          </label>
        )}
        <div
          className={`oh-select__input-wrap custom-input-relative-wrapper${
            showTruncationTip ? " oh-select__input-wrap--tip" : ""
          }`}
        >
          <input
            type="text"
            className={`oh-select__control custom-input${
              invalid ? " is-invalid" : ""
            }${showTruncationTip ? " oh-select__control--with-tip" : ""}`}
            aria-invalid={invalid ? "true" : "false"}
            placeholder={
              isFocused ? selectedName || placeholder : placeholder
            }
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            ref={inputRef}
            disabled={disabled}
          />

          {showTruncationTip && (
            <MDBTooltip tag="span" title={selectedName} placement="top">
              <span
                className="oh-select__tip"
                role="img"
                aria-label={selectedName}
              >
                <img
                  src="/images/information-circle.svg"
                  alt=""
                  width={14}
                  height={14}
                  aria-hidden="true"
                />
              </span>
            </MDBTooltip>
          )}

          {!selectedOptionId && !showDropdown && (
            <span className="oh-select__icon custom-input-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </span>
          )}

          {(showDropdown || selectedOptionId) && showClear && (
            <span
              className="oh-select__clear custom-input-clear"
              onClick={handleClearClick}
              role="button"
              tabIndex={0}
              aria-label={t("clear", { defaultValue: "Clear" })}
            >
              ✕
            </span>
          )}
        </div>
      </div>

      {menuNode}
    </div>
  );
};

export default CustomDropdown;
